import { useState, useEffect } from "react";
import { VideoCamera, Microphone, MapPin, Trash, UploadSimple, Crosshair } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

// Shared field-data panel: site GPS, videos & audios captured on site.
// Used by field executives (MyJobs) and by report preparers (Jobs) — same data.
export default function FieldData({ job, onChange }) {
  const { user } = useAuth();
  const [media, setMedia] = useState(job.field_media || []);
  const [loc, setLoc] = useState(job.site_location || null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => { setMedia(job.field_media || []); setLoc(job.site_location || null); }, [job.id]); // eslint-disable-line

  const canDelete = (m) => user?.role === "owner" || m.uploaded_by === user?.id;
  const videos = media.filter((m) => m.kind === "video");
  const audios = media.filter((m) => m.kind === "audio");

  const upload = async (kind, fileEl) => {
    const file = fileEl.files?.[0];
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    const allowed = kind === "video"
      ? ["mp4", "mov", "webm", "avi", "mkv", "m4v", "3gp"]
      : ["mp3", "wav", "m4a", "aac", "ogg", "oga", "amr", "webm"];
    if (!allowed.includes(ext)) { toast.error(`Unsupported ${kind} file: .${ext}`); if (fileEl) fileEl.value = ""; return; }
    if (file.size > 200 * 1024 * 1024) { toast.error("File too large (max 200 MB)"); if (fileEl) fileEl.value = ""; return; }
    setBusy(true); setProgress(0);
    const uploadId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const CHUNK = 5 * 1024 * 1024;
    const total = Math.ceil(file.size / CHUNK) || 1;
    try {
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
        const fd = new FormData();
        fd.append("file", blob); fd.append("upload_id", uploadId);
        fd.append("chunk_index", i); fd.append("total_chunks", total);
        await api.post(`/jobs/${job.id}/field-media/chunk`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      const ff = new FormData();
      ff.append("upload_id", uploadId); ff.append("kind", kind);
      ff.append("filename", file.name); ff.append("content_type", file.type || "");
      const { data } = await api.post(`/jobs/${job.id}/field-media/finalize`, ff, { headers: { "Content-Type": "multipart/form-data" } });
      setMedia((p) => [...p, data]);
      toast.success(`${kind === "video" ? "Video" : "Audio"} uploaded`);
      onChange?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); setProgress(0); if (fileEl) fileEl.value = ""; }
  };

  const remove = async (m) => {
    try {
      await api.delete(`/jobs/${job.id}/field-media/${m.id}`);
      setMedia((p) => p.filter((x) => x.id !== m.id));
      onChange?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveLoc = async (lat, lng, accuracy) => {
    if (!(lat || lat === 0) || !(lng || lng === 0)) { toast.error("Enter both latitude and longitude"); return; }
    try {
      const { data } = await api.put(`/jobs/${job.id}/site-location`, { lat: Number(lat), lng: Number(lng), accuracy: accuracy ?? null });
      setLoc(data.site_location);
      toast.success("Site location saved");
      onChange?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const captureGps = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not available on this device"); return; }
    toast.loading("Reading GPS…", { id: "gps" });
    navigator.geolocation.getCurrentPosition(
      (pos) => { toast.dismiss("gps"); saveLoc(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy); },
      () => { toast.dismiss("gps"); toast.error("Could not read GPS — enter it manually"); },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const [manLat, setManLat] = useState("");
  const [manLng, setManLng] = useState("");

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-3" data-testid={`field-data-${job.id}`}>
      <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">Field Data</div>
      {busy && (
        <div className="flex items-center gap-2" data-testid={`field-upload-progress-${job.id}`}>
          <div className="h-1.5 flex-1 rounded-full bg-border overflow-hidden"><div className="h-full bg-accent transition-all" style={{ width: `${progress}%` }} /></div>
          <span className="text-[10px] text-muted-foreground tabular-nums">Uploading {progress}%</span>
        </div>
      )}

      {/* Site GPS */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <MapPin size={15} className="text-accent shrink-0" />
          {loc ? (
            <a href={`https://www.google.com/maps/search/?api=1&query=${loc.lat},${loc.lng}`} target="_blank" rel="noreferrer" className="text-xs font-medium text-primary hover:underline" data-testid={`field-gps-${job.id}`}>
              {Number(loc.lat).toFixed(6)}, {Number(loc.lng).toFixed(6)}{loc.accuracy ? ` · ±${Math.round(loc.accuracy)}m` : ""}
            </a>
          ) : <span className="text-xs text-muted-foreground">No site GPS captured yet</span>}
          <Button size="sm" variant="outline" className="h-7 rounded-full text-[11px]" onClick={captureGps} data-testid={`field-gps-capture-${job.id}`}><Crosshair size={13} className="mr-1" /> Capture GPS</Button>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          <input value={manLat} onChange={(e) => setManLat(e.target.value)} placeholder="Latitude" className="h-7 text-[11px] px-2 rounded border border-border flex-1 min-w-24" data-testid={`field-gps-lat-${job.id}`} />
          <input value={manLng} onChange={(e) => setManLng(e.target.value)} placeholder="Longitude" className="h-7 text-[11px] px-2 rounded border border-border flex-1 min-w-24" data-testid={`field-gps-lng-${job.id}`} />
          <Button size="sm" variant="outline" className="h-7 rounded-full text-[11px]" onClick={() => saveLoc(manLat, manLng)} data-testid={`field-gps-save-${job.id}`}>Save</Button>
        </div>
        {loc?.by_name && <div className="text-[10px] text-muted-foreground mt-1">Captured by {loc.by_name}{loc.captured_at ? ` · ${new Date(loc.captured_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}` : ""} · auto-filled valuation lat/lng</div>}
      </div>

      {/* Videos */}
      <div>
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-semibold text-primary inline-flex items-center gap-1"><VideoCamera size={14} className="text-accent" /> Videos ({videos.length})</span>
          <label className="inline-flex items-center gap-1 text-[11px] text-accent hover:underline cursor-pointer" data-testid={`field-video-upload-${job.id}`}>
            <UploadSimple size={13} /> Add video
            <input type="file" accept="video/*" capture="environment" className="hidden" disabled={busy} onChange={(e) => upload("video", e.target)} />
          </label>
        </div>
        {videos.map((m) => (
          <div key={m.id} className="mt-1.5">
            <video controls preload="none" className="w-full max-h-52 rounded-md border border-border bg-black" src={`${API}/jobs/${job.id}/field-media/${m.id}/download`} data-testid={`field-video-${m.id}`} />
            <div className="flex items-center justify-between text-[10px] text-muted-foreground mt-0.5">
              <span className="truncate">{m.filename} · {m.uploaded_by_name}</span>
              {canDelete(m) && <button onClick={() => remove(m)} className="text-destructive hover:underline inline-flex items-center gap-0.5" data-testid={`field-video-del-${m.id}`}><Trash size={11} /> Delete</button>}
            </div>
          </div>
        ))}
      </div>

      {/* Audios */}
      <div>
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-semibold text-primary inline-flex items-center gap-1"><Microphone size={14} className="text-accent" /> Audio notes ({audios.length})</span>
          <label className="inline-flex items-center gap-1 text-[11px] text-accent hover:underline cursor-pointer" data-testid={`field-audio-upload-${job.id}`}>
            <UploadSimple size={13} /> Add audio
            <input type="file" accept="audio/*" capture className="hidden" disabled={busy} onChange={(e) => upload("audio", e.target)} />
          </label>
        </div>
        {audios.map((m) => (
          <div key={m.id} className="flex items-center gap-2 mt-1.5">
            <audio controls preload="none" className="h-8 flex-1" src={`${API}/jobs/${job.id}/field-media/${m.id}/download`} data-testid={`field-audio-${m.id}`} />
            {canDelete(m) && <button onClick={() => remove(m)} className="text-destructive hover:underline text-[10px] inline-flex items-center gap-0.5 shrink-0" data-testid={`field-audio-del-${m.id}`}><Trash size={11} /></button>}
          </div>
        ))}
      </div>
    </div>
  );
}
